<template>
    <div :class="['stepper', {'stepper--disabled': disable}]">
        <div class="stepper__header">
            <template v-for="(step, index) in steps">
                <div :key="index"
                    :class="getHeaderItemClass(index)"
                    @click="navigateToStep(index)"
                >
                    <span class="stepper__number">{{ index + 1 }}</span>
                    <span class="stepper__title">{{ step.name }}</span>
                </div>
                <hr v-if="index < steps.length - 1"
                    :key="index + 'hr'"
                    class="stepper__divider"
                    aria-orientation="horizontal"
                >
            </template>
        </div>
        <div class="stepper__content">
            <component :is="currentComponent"
                v-bind.sync="stepsData"
                @completed="handleStepCompleted"
                @inProgress="handleStepInProgress"
                @disableNavigation="onDisableNavigation"
                @enableNavigation="onEnableNavigation"
            />
        </div>
        <div class="stepper__buttons-wrapper">
            <button
                :class="['stepper__button', 'widget__button', { 'stepper__button--disabled': !currentStepCompleted }]"
                :disabled="!currentStepCompleted"
                @click="submit"
            >
                <span :class="{'stepper__button-icon': !isLastStep}">
                    {{ currentStepButtonText }}
                </span>
            </button>
        </div>
    </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';

export default {
    name: 'stepper',
    props: {
        disable: {
            type: Boolean,
            default: false,
        },
        initialData: {
            type: Object,
            default: () => ({}),
        },
        steps: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            currentStepIndex: 0,
            currentStepCompleted: false,
            navigationEnabled: true,
            stepsData: {},
        };
    },
    computed: {
        currentComponent() {
            return this.steps[this.currentStepIndex].component;
        },
        currentStepButtonText() {
            return this.isLastStep ? this.$t('global.button.submit') : this.$t('global.button.nextStep');
        },
        currentStepRequiresActionToContinue() {
            return this.steps[this.currentStepIndex].requiresActionToContinue;
        },
        isLastStep() {
            return this.currentStepIndex === (this.steps.length - 1);
        },
    },
    beforeMount() {
        this.stepsData = cloneDeep(this.initialData);
    },
    methods: {
        getHeaderItemClass(index) {
            return [
                'stepper__step',
                {
                    'stepper__step--disabled': (index > this.currentStepIndex) || !this.navigationEnabled,
                },
                {
                    'stepper__step--completed': index < this.currentStepIndex,
                },
            ];
        },
        handleStepCompleted() {
            this.currentStepCompleted = true;
        },
        handleStepInProgress() {
            this.currentStepCompleted = false;
        },
        submit() {
            if (this.isLastStep) {
                this.$emit('finished', this.stepsData);

                return;
            }

            if (this.currentStepRequiresActionToContinue) {
                this.$emit('perfomAction', {
                    step: this.currentStepIndex,
                    data: this.stepsData,
                });

                return;
            }

            this.proceedToNextStep();

        },
        proceedToNextStep() {
            this.currentStepIndex++;
            this.handleStepInProgress();
        },
        navigateToStep(index) {
            this.currentStepIndex = index;
        },
        onDisableNavigation() {
            this.navigationEnabled = false;
        },
        onEnableNavigation() {
            this.navigationEnabled = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.stepper {
    color: $daintree-color;

    &--disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    &__header {
        align-items: stretch;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        font-size: 1.5rem;
        font-weight: 700;
        justify-content: space-between;
    }

    &__content {
        background-color: $white-color;
    }

    &__divider {
        align-self: center;
        border: 1px solid $hit-gray-color;
        border-width: thin 0 0;
        display: block;
        flex: 1 1 0;
        height: 0;
        margin: 0 -16px;
        max-height: 0;
        max-width: 100%;
        opacity: 0.3;
    }

    &__step {
        align-items: center;
        display: flex;
        flex-direction: row;
        padding: 24px;
        position: relative;

        &:first-child {
            padding-left: 0;
        }

        &:last-child {
            padding-right: 0;
        }

        &--disabled {
            opacity: 0.5;
            pointer-events: none;
        }

        &--completed {
            cursor: pointer;

            .stepper__number {
                background-color: $white-color;
                border: 2px solid $lime-green-color;
                color: $lime-green-color;
            }
        }
    }

    &__number {
        align-items: center;
        background-color: $lime-green-color;
        border-radius: 50%;
        display: inline-flex;
        height: 30px;
        justify-content: center;
        margin-right: 8px;
        min-width: 30px;
        width: 30px;
    }

    &__title {
        @include max-width(sm) {
            display: none;
        }
    }

    &__buttons-wrapper {
        display: flex;
        justify-content: flex-end;
    }

    &__button {
        background: $lime-green-color;
        color: $daintree-color;
        font-weight: 600;

        @include min-width(md) {
            width: 21.9rem;
        }

        &--disabled {
            opacity: 0.3;
        }
    }

    &__button-icon:after {
        border-color: $daintree-color;
        border-style: solid;
        border-width: 0.2rem 0.2rem 0 0;
        content: "";
        display: inline-block;
        height: 0.8rem;
        position: relative;
        transform: rotate(45deg);
        width: 0.8rem;
    }
}
</style>